/** 管理后台统一日期时间展示（兼容 ISO、时间戳、已有空格格式） */

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export function isIsoDateTimeString(value) {
  return typeof value === 'string' && ISO_DATETIME_RE.test(value.trim());
}

export function formatDisplayDateTime(value) {
  if (value == null || value === '') return value ?? '';
  if (typeof value === 'number') {
    if (!isPlausibleTimestamp(value)) return String(value);
    const ms = value >= 1e12 ? value : value * 1000;
    const date = new Date(ms);
    if (Number.isNaN(date.getTime())) return String(value);
    return formatDateFromDate(date, true);
  }
  const raw = String(value).trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw)) {
    return raw.length === 16 ? `${raw}:00` : raw.substring(0, 19);
  }
  if (raw.includes('T')) {
    const normalized = raw
      .replace('T', ' ')
      .replace(/\.\d+/, '')
      .replace(/Z$/i, '')
      .replace(/([+-]\d{2}:?\d{2})$/, '');
    return normalized.length === 16 ? `${normalized}:00` : normalized.substring(0, 19);
  }
  return raw;
}

export function formatDisplayDate(value) {
  const formatted = formatDisplayDateTime(value);
  if (!formatted) return formatted ?? '';
  return String(formatted).split(' ')[0];
}

/** 仅展示 HH:mm（兼容 ISO、时间戳、完整日期时间） */
export function formatDisplayTime(value) {
  const formatted = formatDisplayDateTime(value);
  if (!formatted) return formatted ?? '';
  const raw = String(formatted);
  if (/^\d{2}:\d{2}/.test(raw)) return raw.substring(0, 5);
  const timePart = raw.includes(' ') ? raw.split(' ')[1] : '';
  return timePart ? timePart.substring(0, 5) : raw;
}

function formatDateFromDate(date, withSeconds) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return withSeconds ? `${y}-${m}-${d} ${h}:${min}:${s}` : `${y}-${m}-${d} ${h}:${min}`;
}

/** 递归将接口返回中的 ISO 时间串转为 yyyy-MM-dd HH:mm:ss */
export function normalizeResponseDates(payload) {
  if (payload == null) return payload;
  if (Array.isArray(payload)) {
    return payload.map(normalizeResponseDates);
  }
  if (typeof payload === 'object') {
    const next = {};
    for (const [key, val] of Object.entries(payload)) {
      next[key] = normalizeResponseDates(val);
    }
    return next;
  }
  if (isIsoDateTimeString(payload)) {
    return formatDisplayDateTime(payload);
  }
  return payload;
}

const DATE_COLUMN_RE = /(?:^|_)(at|time|date)$/i;

/** 业务上的工时/时长字段（数值小时），勿按日期时间格式化 */
const NON_DATE_TIME_PROPS = new Set([
  'standard_time',
  'ot_time',
  'zhi_time',
  'tongxiao_time',
  'total_time',
  'chu_time',
  'time',
]);

function isPlausibleTimestamp(value) {
  if (!Number.isFinite(value)) return false;
  const abs = Math.abs(value);
  // 秒级时间戳（约 2001 年起）
  if (abs >= 1e9 && abs < 1e12) return true;
  // 毫秒级时间戳
  return abs >= 1e12;
}

export function isDateColumnProp(prop) {
  if (typeof prop !== 'string' || NON_DATE_TIME_PROPS.has(prop)) return false;
  return DATE_COLUMN_RE.test(prop);
}
